import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import { useTheme } from '@mui/system';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing } from 'utils/constants';
import ReportFileExports from 'components/Reports/FileExports';

const BatteryHealthDevicesDialog = ({ onClose, startDate, endDate, groupId, columnIndex, groupsData, xLabel, selectedStatements }) => {
    const theme = useTheme();
    const [batteryHealthDevicesData, setBatteryHealthDevicesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtersData, setFiltersData] = useState([]);

    useEffect(() => {
        setLoading(true);
        ApiHelper.post('/api/reports/get_batterycap_devices/', {
            start_date: startDate,
            end_date: endDate,
            group_id: groupId === 0 ? '' : groupId.toString(),
            column_index: columnIndex,
            statement: selectedStatements.length > 0 ? selectedStatements : []
        })
            .then((res) => {
                setLoading(false);
                setBatteryHealthDevicesData(res.data);
                toast.success('Successfully done');
                let groupName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                const temp = [
                    { label: 'Start Date', value: startDate },
                    { label: 'End Date', value: endDate },
                    { label: 'Group Name', value: groupName },
                    { label: 'Battery Charge(%)', value: xLabel }
                ];
                setFiltersData(temp);
            })
            .catch(() => {
                setLoading(false);
                toast.warning('There is no battery health statistics data');
            });
    }, [columnIndex, endDate, groupId, groupsData, startDate, xLabel, selectedStatements]);

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Show Battery Health Devices</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <Grid container spacing={gridSpacing}>
                        {batteryHealthDevicesData.length > 0 && (
                            <Grid item xs={12}>
                                <ReportFileExports
                                    elementId="batteryHealthDevicesData"
                                    reportTitle="Battery Health Devices"
                                    downloadFileName="battery_health_devices"
                                    downloadFileOption="all"
                                    data={batteryHealthDevicesData}
                                    headers={[
                                        { label: 'Device Name', key: 'name' },
                                        { label: 'MAC Address', key: 'mac' },
                                        { label: 'IP Address', key: 'ipv4' },
                                        { label: 'Battery Charge Capacity', key: 'usage' }
                                    ]}
                                    filtersData={filtersData}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Paper sx={{ overflowX: 'auto' }}>
                                {loading === true ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                        Please wait...
                                    </Typography>
                                ) : batteryHealthDevicesData.length === 0 ? (
                                    <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                        No data to show the battery health devices
                                    </Typography>
                                ) : (
                                    <Table id="batteryHealthDevicesData">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Device Name</TableCell>
                                                <TableCell>MAC Address</TableCell>
                                                <TableCell>IP Address</TableCell>
                                                <TableCell>Battery Charge Capacity</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {batteryHealthDevicesData.map((data, key) => (
                                                <TableRow key={key}>
                                                    <TableCell>{data.name}</TableCell>
                                                    <TableCell>{data.mac}</TableCell>
                                                    <TableCell>{data.ipv4}</TableCell>
                                                    <TableCell>{data.usage}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                {loading === false && (
                    <Button onClick={onClose} color="primary" variant="contained">
                        OK
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

BatteryHealthDevicesDialog.propTypes = {
    onClose: PropTypes.func,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    groupId: PropTypes.number,
    columnIndex: PropTypes.number,
    groupsData: PropTypes.array,
    xLabel: PropTypes.string,
    selectedStatements: PropTypes.array
};

export default BatteryHealthDevicesDialog;
